/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/api/index.ts
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS API client functions
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
    SmsProvider,
    SmsNumber,
    SmsRoute,
    SmsMessage,
    SmsSettings,
    SmsOptout,
    SmsProviderFormData,
    SmsNumberFormData,
    SmsRouteFormData,
    SendSmsFormData
} from '../types';

const SMS_API = '/api/sms';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const response = await fetch(buildApiUrl(endpoint), {
        ...options,
        headers: {
            ...getApiHeaders(),
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Request failed');
    }

    return data;
}

// ========================================
// Providers API
// ========================================

export async function fetchProviders(): Promise<SmsProvider[]> {
    const response = await request<SmsProvider[]>(`${SMS_API}/providers`);
    return response.data || [];
}

export async function fetchProvider(id: number | string): Promise<SmsProvider | null> {
    const response = await request<SmsProvider>(`${SMS_API}/providers/${id}`);
    return response.data || null;
}

export async function createProvider(data: SmsProviderFormData): Promise<SmsProvider> {
    const response = await request<SmsProvider>(`${SMS_API}/providers`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function updateProvider(id: number | string, data: Partial<SmsProviderFormData>): Promise<SmsProvider> {
    const response = await request<SmsProvider>(`${SMS_API}/providers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function deleteProvider(id: number | string): Promise<void> {
    await request(`${SMS_API}/providers/${id}`, { method: 'DELETE' });
}

export async function testProvider(id: number | string): Promise<{ connected: boolean; latencyMs?: number }> {
    const response = await request<{ connected: boolean; latencyMs?: number }>(`${SMS_API}/providers/${id}/test`, {
        method: 'POST',
    });
    return response.data!;
}

// ========================================
// Numbers API
// ========================================

export async function fetchNumbers(): Promise<SmsNumber[]> {
    const response = await request<SmsNumber[]>(`${SMS_API}/numbers`);
    return response.data || [];
}

export async function fetchNumber(id: number | string): Promise<SmsNumber | null> {
    const response = await request<SmsNumber>(`${SMS_API}/numbers/${id}`);
    return response.data || null;
}

export async function createNumber(data: SmsNumberFormData): Promise<SmsNumber> {
    const response = await request<SmsNumber>(`${SMS_API}/numbers`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function updateNumber(id: number | string, data: Partial<SmsNumberFormData>): Promise<SmsNumber> {
    const response = await request<SmsNumber>(`${SMS_API}/numbers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function deleteNumber(id: number | string): Promise<void> {
    await request(`${SMS_API}/numbers/${id}`, { method: 'DELETE' });
}

// ========================================
// Routes API
// ========================================

export async function fetchRoutes(): Promise<SmsRoute[]> {
    const response = await request<SmsRoute[]>(`${SMS_API}/routes`);
    return response.data || [];
}

export async function fetchRoute(id: number | string): Promise<SmsRoute | null> {
    const response = await request<SmsRoute>(`${SMS_API}/routes/${id}`);
    return response.data || null;
}

export async function createRoute(data: SmsRouteFormData): Promise<SmsRoute> {
    const response = await request<SmsRoute>(`${SMS_API}/routes`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function updateRoute(id: number | string, data: Partial<SmsRouteFormData>): Promise<SmsRoute> {
    const response = await request<SmsRoute>(`${SMS_API}/routes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return response.data!;
}

export async function deleteRoute(id: number | string): Promise<void> {
    await request(`${SMS_API}/routes/${id}`, { method: 'DELETE' });
}

// ========================================
// Messages API
// ========================================

interface FetchMessagesParams {
    page?: number;
    limit?: number;
    direction?: 'inbound' | 'outbound';
    status?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
}

export async function fetchMessages(params: FetchMessagesParams = {}): Promise<{
    messages: SmsMessage[];
    pagination: { page: number; limit: number; total: number; pages: number };
}> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.direction) query.set('direction', params.direction);
    if (params.status) query.set('status', params.status);
    if (params.fromDate) query.set('fromDate', params.fromDate);
    if (params.toDate) query.set('toDate', params.toDate);
    if (params.search) query.set('search', params.search);

    const response = await request<SmsMessage[]>(`${SMS_API}/messages?${query.toString()}`);
    return {
        messages: response.data || [],
        pagination: response.pagination || { page: 1, limit: 50, total: 0, pages: 0 },
    };
}

export async function fetchMessage(id: string): Promise<SmsMessage | null> {
    const response = await request<SmsMessage>(`${SMS_API}/messages/${id}`);
    return response.data || null;
}

export async function sendMessage(data: SendSmsFormData): Promise<{
    messageId: string;
    status: string;
    segments: number;
    encoding: string;
}> {
    const response = await request<{
        messageId: string;
        status: string;
        segments: number;
        encoding: string;
    }>(`${SMS_API}/messages/send`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.data!;
}

// ========================================
// Settings API
// ========================================

export async function fetchSettings(): Promise<SmsSettings> {
    const response = await request<SmsSettings>(`${SMS_API}/settings`);
    return response.data!;
}

export async function updateSettings(data: Partial<SmsSettings>): Promise<SmsSettings> {
    const response = await request<SmsSettings>(`${SMS_API}/settings`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return response.data!;
}

// ========================================
// Optouts API
// ========================================

export async function fetchOptouts(): Promise<SmsOptout[]> {
    const response = await request<SmsOptout[]>(`${SMS_API}/optouts`);
    return response.data || [];
}

export async function addOptout(phoneNumber: string, reason?: string): Promise<SmsOptout> {
    const response = await request<SmsOptout>(`${SMS_API}/optouts`, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber, reason }),
    });
    return response.data!;
}

export async function removeOptout(id: number | string): Promise<void> {
    await request(`${SMS_API}/optouts/${id}`, { method: 'DELETE' });
}
